import React from 'react';
import { Helmet } from 'react-helmet-async';

export default function PetiteEnfance() {
  const jsonLdBreadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://www.loustaletformation.fr/" },
      { "@type": "ListItem", "position": 2, "name": "Formations Petite Enfance", "item": "https://www.loustaletformation.fr/petite-enfance" }
    ]
  };

  // Liste des parcours principaux proposés sur la thématique
  const jsonLdItemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "item": {
          "@type": "Course",
          "name": "VAE Auxiliaire de puériculture (DEAP)",
          "url": "https://www.loustaletformation.fr/vae-auxiliaire-de-puericulture",
          "provider": { "@type": "Organization", "name": "L’Oustalet Formation", "sameAs": "https://www.loustaletformation.fr/" }
        }
      },
      {
        "@type": "ListItem",
        "position": 2,
        "item": {
          "@type": "Course",
          "name": "Comprendre la VAE Petite Enfance",
          "url": "https://www.loustaletformation.fr/vae",
          "provider": { "@type": "Organization", "name": "L’Oustalet Formation", "sameAs": "https://www.loustaletformation.fr/" }
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Formations Petite Enfance | L’Oustalet Formation</title>
        <meta
          name="description"
          content="Vous souhaitez travailler avec les tout-petits, évoluer en crèche, à l’hôpital ou en pouponnière ? Découvrez notre accompagnement pour obtenir votre diplôme d’auxiliaire de puériculture (VAE DEAP) et comprendre la VAE Petite Enfance."
        />
        <link rel="canonical" href="https://www.loustaletformation.fr/petite-enfance" />

        {/* Open Graph */}
        <meta property="og:title" content="Formations Petite Enfance | L’Oustalet Formation" />
        <meta property="og:description" content="VAE DEAP et Comprendre la VAE Petite Enfance. Préparation mixte avec cours, vidéos, QCM et RDV individuels. 15€ à 35€ / mois." />
        <meta property="og:url" content="https://www.loustaletformation.fr/petite-enfance" />
        <meta property="og:type" content="website" />

        {/* Données structurées */}
        <script type="application/ld+json">{JSON.stringify(jsonLdBreadcrumb)}</script>
        <script type="application/ld+json">{JSON.stringify(jsonLdItemList)}</script>
      </Helmet>

      <section className="px-6 py-10 max-w-5xl mx-auto">
        {/* Fil d’Ariane */}
        <nav className="text-sm text-gray-500 mb-6" aria-label="Fil d’Ariane">
          <a href="/" className="underline">Accueil</a> <span className="mx-2">›</span> <span>Formations Petite Enfance</span>
        </nav>

        <h1 className="text-4xl sm:text-5xl font-bold text-rose-600 mb-4">Formations Petite Enfance</h1>
        <p className="text-lg text-gray-700 mb-8">
          Vous souhaitez travailler avec les tout-petits, évoluer en crèche, à l’hôpital ou en pouponnière&nbsp;?
          Découvrez notre accompagnement pour obtenir votre diplôme d’auxiliaire de puériculture&nbsp;!
        </p>

        {/* Parcours proposés */}
        <h2 className="text-2xl font-semibold mb-4">Nos parcours</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <a href="/vae-auxiliaire-de-puericulture" className="block border rounded-xl p-5 hover:shadow-md transition-shadow">
            <h3 className="text-xl font-semibold mb-2">VAE Auxiliaire de puériculture (DEAP)</h3>
            <p className="text-gray-700 text-sm">
              Valorisez votre expérience pour obtenir le DEAP&nbsp;: Livret 1, Livret 2, préparation à l’oral de jury, assistance aux financements.
            </p>
          </a>

          <a href="/vae" className="block border rounded-xl p-5 hover:shadow-md transition-shadow">
            <h3 className="text-xl font-semibold mb-2">Comprendre la VAE Petite Enfance</h3>
            <p className="text-gray-700 text-sm">
              Les bases de la VAE&nbsp;: éligibilité, étapes (L1/L2), calendrier, financements (CPF) et bonnes pratiques pour réussir.
            </p>
          </a>
        </div>

        {/* Panorama métiers */}
        <h2 className="text-2xl font-semibold mb-3">Panorama des métiers</h2>
        <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
          <li><strong>Auxiliaire de puériculture</strong> (DEAP)&nbsp;: soins, hygiène, sécurité, accompagnement du développement, relation aux familles. Soutien aux actes médicaux.</li>
          <li><strong>ATSEM / Aide en école maternelle</strong>&nbsp;: appui à l’enseignant·e, hygiène, accueil des enfants.</li>
          <li><strong>Assistant·e maternel·le / garde d’enfants</strong>&nbsp;: accueil au domicile, activités d’éveil, sécurité et développement.</li>
          <li><strong>Agent·e petite enfance en EAJE</strong> (CAP AEPE)&nbsp;: accueil, soins d’hygiène, activités, entretien des espaces.</li>
        </ul>

        {/* Pour qui ? */}
        <h2 className="text-2xl font-semibold mb-3">Pour qui sont ces parcours&nbsp;?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="border rounded-xl p-5">
            <h3 className="text-lg font-semibold mb-2">Vous avez déjà de l’expérience</h3>
            <p className="text-gray-700 text-sm mb-2">
              La <a href="/vae-auxiliaire-de-puericulture" className="underline text-rose-700">VAE DEAP</a> permet de transformer votre vécu professionnel en diplôme d’État.
            </p>
            <p className="text-gray-700 text-sm">
              Nous avons créé <strong>Loustalet Formation</strong> pour vous aider à chaque étape de ce parcours.
            </p>
          </div>
        </div>

        {/* Financements */}
        <h2 className="text-2xl font-semibold mb-3">Financements & accompagnement</h2>
        <p className="text-gray-700 mb-4">
          Une préparation <strong>mixte</strong> avec Loustalet Formation, en ligne, avec cours, vidéos, QCM d’entraînements et possibilité de réserver des <strong>RDV individuels</strong> pour les moments clefs de votre parcours de VAE si besoin uniquement.
          <br />
          <strong>Tarif&nbsp;:</strong> entre <strong>15€</strong> et <strong>35€</strong> par mois selon le format choisi.
        </p>
        <div className="flex flex-wrap gap-3 mb-10">
          <a href="/tarifs" className="inline-block px-5 py-3 rounded-2xl bg-rose-600 text-white font-semibold shadow">
            Voir les tarifs
          </a>
          <a href="/vae" className="inline-block px-5 py-3 rounded-2xl border border-rose-600 text-rose-700 font-semibold">
            En savoir plus sur la VAE
          </a>
        </div>

        {/* Lien vers la FAQ */}
<h2 className="text-2xl font-semibold mb-3">FAQ</h2>
<p className="text-gray-700 mb-4">
  Retrouvez toutes les réponses à vos questions sur la VAE d’auxiliaire de puériculture,
  les financements, les blocs de compétences et plus encore.
</p>
<a
  href="/faq"
  className="inline-block px-5 py-3 rounded-2xl border border-rose-600 text-rose-700 font-semibold hover:bg-rose-50 transition"
>
  Consulter la FAQ complète
</a>
      </section>
    </>
  );
}
